import axios from "axios";
import { getValidToken } from "./authService";
import {
  isDemoMode,
  demoGetBrokers,
  demoGetHoldings,
  demoCreateHolding,
  demoDeleteHolding,
  demoGetPositions,
  demoGetStockPrices,
  demoRefreshPricesBatch,
  demoGetPortfolioSummary,
  demoGetPortfolioHistory,
  demoGetHoldingsByBroker,
  demoGetAllPositionDetails,
  demoGetFXRates,
  demoGetUserTickers,
  demoGetStockTickers,
  demoGetStockFundamentals,
  demoGetStockPriceHistory,
  demoGetStockNews,
  demoGetStockPrice,
} from "../demo/demoStore";

const API_URL = process.env.REACT_APP_API_URL;

const apiClient = axios.create({
  baseURL: API_URL,
});

apiClient.interceptors.request.use(
  async (config) => {
    const token = await getValidToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// ============================================
// BROKERS
// ============================================

export const getBrokers = async () => {
  if (isDemoMode()) return demoGetBrokers();
  try {
    const response = await apiClient.get("/brokers");
    return response.data;
  } catch (error) {
    console.error("Error fetching brokers:", error);
    throw error;
  }
};

export const getBroker = async (brokerId) => {
  if (isDemoMode()) { const brokers = await demoGetBrokers(); return brokers.find(b => b.id === brokerId) || null; }
  try {
    const response = await apiClient.get(`/brokers/${brokerId}`);
    return response.data;
  } catch (error) {
    console.error("Error fetching broker:", error);
    throw error;
  }
};

export const createBroker = async (brokerData) => {
  if (isDemoMode()) return Promise.resolve({ id: 99, ...brokerData });
  try {
    const response = await apiClient.post("/brokers", brokerData);
    return response.data;
  } catch (error) {
    console.error("Error creating broker:", error);
    throw error;
  }
};

export const updateBroker = async (brokerId, brokerData) => {
  if (isDemoMode()) return Promise.resolve({ success: true });
  try {
    const response = await apiClient.put(`/brokers/${brokerId}`, brokerData);
    return response.data;
  } catch (error) {
    console.error("Error updating broker:", error);
    throw error;
  }
};

export const deleteBroker = async (brokerId) => {
  if (isDemoMode()) return Promise.resolve({ success: true });
  try {
    const response = await apiClient.delete(`/brokers/${brokerId}`);
    return response.data;
  } catch (error) {
    console.error("Error deleting broker:", error);
    throw error;
  }
};

// ============================================
// HOLDINGS
// ============================================

export const getHoldings = async (brokerId = null) => {
  if (isDemoMode()) return demoGetHoldings(brokerId);
  try {
    const params = brokerId ? { broker_id: brokerId } : {};
    const response = await apiClient.get("/holdings", { params });
    return response.data;
  } catch (error) {
    console.error("Error fetching holdings:", error);
    throw error;
  }
};

export const getHolding = async (holdingId) => {
  if (isDemoMode()) { const holdings = await demoGetHoldings(); return holdings.find(h => h.id === holdingId) || null; }
  try {
    const response = await apiClient.get(`/holdings/${holdingId}`);
    return response.data;
  } catch (error) {
    console.error("Error fetching holding:", error);
    throw error;
  }
};

export const createHolding = async (holdingData) => {
  if (isDemoMode()) return demoCreateHolding(holdingData);
  try {
    const response = await apiClient.post("/holdings", holdingData);
    return response.data;
  } catch (error) {
    console.error("Error creating holding:", error);
    throw error;
  }
};

export const updateHolding = async (holdingId, holdingData) => {
  if (isDemoMode()) return Promise.resolve({ success: true });
  try {
    const response = await apiClient.put(`/holdings/${holdingId}`, holdingData);
    return response.data;
  } catch (error) {
    console.error("Error updating holding:", error);
    throw error;
  }
};

export const deleteHolding = async (holdingId) => {
  if (isDemoMode()) return demoDeleteHolding(holdingId);
  try {
    const response = await apiClient.delete(`/holdings/${holdingId}`);
    return response.data;
  } catch (error) {
    console.error("Error deleting holding:", error);
    throw error;
  }
};

// ============================================
// POSITIONS
// ============================================

export const getPositions = async (brokerId = null) => {
  if (isDemoMode()) return demoGetPositions();
  try {
    const params = brokerId ? { broker_id: brokerId } : {};
    const response = await apiClient.get("/positions", { params });
    return response.data;
  } catch (error) {
    console.error("Error fetching positions:", error);
    throw error;
  }
};

export const getPositionDetails = async (ticker, brokerId) => {
  try {
    if (!brokerId) {
      throw new Error("brokerId is required for getPositionDetails");
    }
    const response = await apiClient.get(
      `/position-details/${ticker}/${brokerId}`,
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching position details:", error);
    throw error;
  }
};

export const getAllPositionDetails = async () => {
  if (isDemoMode()) return demoGetAllPositionDetails();
  try {
    const response = await apiClient.get("/position-details-all");
    return response.data; // already a map: { "TICKER-brokerid": [...] }
  } catch (error) {
    console.error("Error fetching all position details:", error);
    throw error;
  }
};

// ============================================
// STOCK PRICES
// ============================================

export const getStockPrices = async (tickers) => {
  if (isDemoMode()) return demoGetStockPrices(tickers);
  try {
    const tickerString = Array.isArray(tickers) ? tickers.join(",") : tickers;
    const response = await apiClient.get("/prices", {
      params: { tickers: tickerString },
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching stock prices:", error);
    throw error;
  }
};

export const updateAllPrices = async (force = false) => {
  try {
    const response = await apiClient.post("/prices/update", { force });
    return response.data;
  } catch (error) {
    console.error("Error updating prices:", error);
    throw error;
  }
};

/**
 * Refresh prices for a list of tickers.
 * The backend checks cache in one DB query, fetches stale ones in parallel,
 * and returns all prices in a single response.
 * @param {string[]} tickers
 * @returns {Object[]} Array of price objects
 */
export const refreshPricesBatch = async (tickers) => {
  if (isDemoMode()) return demoRefreshPricesBatch(tickers);
  try {
    const response = await apiClient.post("/prices/refresh-batch", { tickers });
    return response.data;
  } catch (error) {
    console.error("Error refreshing prices batch:", error);
    throw error;
  }
};

export const getStockPrice = async (ticker) => {
  if (isDemoMode()) return demoGetStockPrice(ticker);
  try {
    const response = await apiClient.get(`/prices/${ticker}`);
    return response.data;
  } catch (error) {
    console.error("Error fetching stock price:", error);
    throw error;
  }
};

export const getIntradayData = async (ticker) => {
  try {
    const response = await apiClient.get(`/prices/${ticker}/intraday`);
    return response.data;
  } catch (error) {
    console.error("Error fetching intraday data:", error);
    throw error;
  }
};

// range: "1mo" | "3mo" | "6mo" | "1y" | "2y" | "5y"
export const getStockPriceHistory = async (ticker, range = "1y") => {
  if (isDemoMode()) return demoGetStockPriceHistory(ticker);
  try {
    const response = await apiClient.get(`/prices/${ticker}/history`, {
      params: { range },
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching price history:", error);
    throw error;
  }
};

export const getStockFundamentals = async (ticker) => {
  if (isDemoMode()) return demoGetStockFundamentals(ticker);
  try {
    const response = await apiClient.get(`/stocks/${ticker}/fundamentals`);
    return response.data;
  } catch (error) {
    console.error("Error fetching fundamentals:", error);
    throw error;
  }
};

export const getStockNews = async (ticker, count = 8) => {
  if (isDemoMode()) return demoGetStockNews(ticker);
  try {
    const response = await apiClient.get(`/stocks/${ticker}/news`, {
      params: { count },
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching stock news:", error);
    throw error;
  }
};

// Seed FX_RATES_HISTORY with ECB daily rates for all currency pairs used by
// the authenticated user. Call this before backfillPortfolioHistory so the
// SQL function can apply period-accurate FX rates to each snapshot date.
// startDate / endDate: "YYYY-MM-DD" strings (both optional, server defaults to 2y)
// Ensure STOCK_PRICE_HISTORY is populated for all user tickers over the given range.
// Must be called before backfillPortfolioHistory so recent dates (e.g. Apr 30, May 1)
// that have no price rows get fetched from Yahoo Finance and stored.
export const refreshPriceHistory = async (range = "5y") => {
  try {
    const response = await apiClient.post(`/portfolio/prices/refresh-history`, null, {
      params: { range },
      timeout: 120000,
    });
    return response.data;
  } catch (error) {
    console.error("Error refreshing price history:", error);
    throw error;
  }
};

export const seedFxHistory = async ({ startDate, endDate } = {}) => {
  try {
    const params = {};
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    const response = await apiClient.post(`/portfolio/fx/seed-history`, null, { params });
    return response.data;
  } catch (error) {
    console.error("Error seeding FX history:", error);
    throw error;
  }
};

export const backfillPortfolioHistory = async (days = 365) => {
  try {
    const response = await apiClient.post(`/portfolio/history/backfill`, null, { params: { days } });
    return response.data;
  } catch (error) {
    console.error("Error backfilling portfolio history:", error);
    throw error;
  }
};

// days: 7–1825; defaults to 90
export const getPortfolioHistory = async (days = 90) => {
  if (isDemoMode()) return demoGetPortfolioHistory();
  try {
    const response = await apiClient.get("/portfolio/history", {
      params: { days },
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching portfolio history:", error);
    throw error;
  }
};

// ============================================
// PORTFOLIO ANALYTICS
// ============================================

export const getPortfolioSummary = async () => {
  if (isDemoMode()) return demoGetPortfolioSummary();
  try {
    const response = await apiClient.get("/portfolio/summary");
    return response.data;
  } catch (error) {
    console.error("Error fetching portfolio summary:", error);
    throw error;
  }
};

export const getHoldingsByBroker = async () => {
  if (isDemoMode()) return demoGetHoldingsByBroker();
  try {
    const response = await apiClient.get("/portfolio/by-broker");
    return response.data;
  } catch (error) {
    console.error("Error fetching holdings by broker:", error);
    throw error;
  }
};

export const getUserTickers = async () => {
  if (isDemoMode()) return demoGetUserTickers();
  try {
    const response = await apiClient.get("/portfolio/tickers");
    return response.data;
  } catch (error) {
    console.error("Error fetching user tickers:", error);
    throw error;
  }
};

export const getStockPosition = async (ticker, brokerId) => {
  try {
    const response = await apiClient.get("/portfolio/position", {
      params: { ticker, broker_id: brokerId },
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching stock position:", error);
    throw error;
  }
};

// Get user's stock tickers for autocomplete
export const getStockTickers = async () => {
  if (isDemoMode()) return demoGetStockTickers();
  try {
    const response = await apiClient.get("/stock-tickers");
    return response.data;
  } catch (error) {
    console.error("Error fetching stock tickers:", error);
    return [];
  }
};

export const getFXRates = async () => {
  if (isDemoMode()) return demoGetFXRates();
  const response = await apiClient.get("/fx-rates");
  return response.data;
};

export const getBonds = async () => {
  if (isDemoMode()) return Promise.resolve([]);
  const response = await apiClient.get("/bonds");
  return response.data;
};

export const getBondPositions = async () => {
  if (isDemoMode()) return Promise.resolve([]);
  const response = await apiClient.get("/bond-positions");
  return response.data;
};

export const createBond = async (finalData) => {
  if (isDemoMode()) return Promise.resolve({ success: true });
  try {
    const response = await apiClient.post("/bonds", finalData);
    return response.data;
  } catch (error) {
    console.error("Error creating bond:", error);
    throw error;
  }
};

export const updateBond = async (currentBondId, finalData) => {
  if (isDemoMode()) return Promise.resolve({ success: true });
  try {
    const response = await apiClient.put(`/bonds/${currentBondId}`, finalData);
    return response.data;
  } catch (error) {
    console.error("Error updating bond:", error);
    throw error;
  }
};

export const deleteBond = async (id) => {
  if (isDemoMode()) return Promise.resolve({ success: true });
  try {
    const response = await apiClient.delete(`/bonds/${id}`);
    return response.data;
  } catch (error) {
    console.error("Error deleting bond:", error);
    throw error;
  }
};

export const getCashflows = async () => {
  if (isDemoMode()) return Promise.resolve([]);
  const response = await apiClient.get("/cash-flows");
  return response.data;
};

export const getCashflowsSummary = async () => {
  if (isDemoMode()) return Promise.resolve([]);
  const response = await apiClient.get("/cash-flows/summary");
  return response.data;
};

export const createCashflow = async (finalData) => {
  try {
    const response = await apiClient.post("/cash-flows", finalData);
    return response.data;
  } catch (error) {
    console.error("Error creating cashflow:", error);
    throw error;
  }
};

export const updateCashflow = async (currentCashflowId, finalData) => {
  try {
    const response = await apiClient.put(
      `/cash-flows/${currentCashflowId}`,
      finalData,
    );
    return response.data;
  } catch (error) {
    console.error("Error updating cashflow:", error);
    throw error;
  }
};

export const deleteCashflow = async (id) => {
  try {
    const response = await apiClient.delete(`/cash-flows/${id}`);
    return response.data;
  } catch (error) {
    console.error("Error deleting cashflow:", error);
    throw error;
  }
};
